import React, { useState } from "react";
import { Tooltip, FormFeedback } from "reactstrap";

const MultiCheckboxGroup = ({
    label = "Select Options", // Default label
    options = [], // Options array for checkboxes
    required = false, // Is the group required?
    maxSelections = null, // Maximum selections allowed (null for no limit)
    onChange = () => {}, // onChange handler
    showHelp = false, // Show help icon?
    helpText = "", // Help tooltip text
    requiredErrorMessage = "Please select at least one option.", // Default required error message
    maxSelectionErrorMessage = (max) => `You can select up to ${max} options.`, // Max selection error message
    customError = null, // Custom error messages if needed
    layout = "below" // Layout style (inline or below)
}) => {
    const [selectedOptions, setSelectedOptions] = useState([]); // Manage selected options state
    const [errors, setErrors] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    // Handle checkbox change
    const handleChange = (option) => {
        const newSelectedOptions = selectedOptions.includes(option)
            ? selectedOptions.filter((item) => item !== option) // Unselect if already selected
            : [...selectedOptions, option]; // Select if not already selected

        if (maxSelections && newSelectedOptions.length > maxSelections) {
            return; // Do not update if max selections exceeded
        }

        setSelectedOptions(newSelectedOptions); // Update selected options state
        setErrors(false); // Clear error state
        onChange(newSelectedOptions); // Trigger external onChange
    };

    // Validate selection on form submission
    const validateSelection = () => {
        if (required && selectedOptions.length === 0) {
            setErrors(true); // Set error if required and no selection
        } else if (maxSelections && selectedOptions.length > maxSelections) {
            setErrors(true); // Set error if selections exceed max
        }
    };

    return (
        <div>
            <label className="d-flex" style={{ alignItems: 'center' }}>
                {label}{required && <span className="text-danger">*</span>}
                {showHelp && (
                    <span id={`help-${label.replace(/\s+/g, '-').toLowerCase()}`} className="px-2" style={{ cursor: 'pointer' }} onMouseEnter={() => setTooltipOpen(true)} onMouseLeave={() => setTooltipOpen(false)}>
                        <i className="ri-question-line" style={{ fontSize: '16px' }} />
                    </span>
                )}
            </label>
            {showHelp && (
                <Tooltip
                    placement="right"
                    isOpen={tooltipOpen}
                    target={`help-${label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                    {helpText}
                </Tooltip>
            )}
            <div className={layout === "inline" ? "d-flex" : ""}>
                {options.map((option, index) => (
                    <div key={index} className={`form-check ${layout === "inline" ? "me-3" : ""}`}>
                        <input
                            type="checkbox"
                            id={`option-${index}`}
                            className="form-check-input"
                            checked={selectedOptions.includes(option)}
                            onChange={() => handleChange(option)}
                            onBlur={validateSelection} // Validate on blur
                        />
                        <label className="form-check-label" htmlFor={`option-${index}`}>
                            {option}
                        </label>
                    </div>
                ))}
            </div>
            {errors && (
                <FormFeedback type="invalid">
                    {customError || (required ? requiredErrorMessage : maxSelectionErrorMessage(maxSelections))}
                </FormFeedback>
            )}
        </div>
    );
};

export default MultiCheckboxGroup;
